import { InputGroupField, Property, StringMap } from '@/types';
// eslint-disable-next-line import/no-cycle
import { generateTranslations } from '@/components/template-builder/utils/translate';
import { TRANSLATION_ATTRIBUTE } from '@/components/template-builder/utils/constants';

import * as cheerio from 'cheerio';

// eslint-disable-next-line import/no-cycle
import {
  getAttributeByName, getClassByName, getCustomDescription, getCustomUrl, getLinkByNetwork, getSocialNetworkIconStyle, getSocialNetworkIconStyleV4,
} from '../getters';
import { CUSTOMIZABLE_ICON_STYLES_V4 } from '@/types';
// eslint-disable-next-line import/no-cycle
import { getTemplateIframeDocument } from '../utils/helpers';
// eslint-disable-next-line import/no-cycle
import { updateSectionsInState, removeUnusedTranslations } from '@/composables/template-editor/TemplateEditor';
// eslint-disable-next-line import/no-cycle
import { removeDynamicStyle, addOrUpdateDynamicStyle } from '../utils/parser';

const DEFAULT_WIDTH = '24';

const facebookPattern = /^(https:\/\/www.facebook.com\/sharer\/sharer.php\?u=)(.*)$/i;
const twitterPattern = /^(https:\/\/twitter.com\/intent\/tweet\?text=)(.*)(&[amp;]*url=)(.*)$/i;
const emailPattern = /^(mailto:\?subject=)(.*)(&[amp;]*body=)(.*)$/i;
const googlePattern = /^(https:\/\/plus.google.com\/share\?url=)(.*)$/i;

interface Network {
  name: string;
  icon: HTMLAnchorElement;
}

/** GLOBAL SOCIAL NETWORK SETTERS */
export const setSocialNetworkIconWidth = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();

  // Supprimer les styles width/max-width de toutes les feuilles de style dynamiques
  removeDynamicStyle(selector, { width: '' }, false, false);
  removeDynamicStyle(selector, { 'max-width': '' }, false, false);
  removeDynamicStyle(selector, { width: '' }, false, 'dynamic_persistent');
  removeDynamicStyle(selector, { 'max-width': '' }, false, 'dynamic_persistent');

  // Supprimer les styles inline existants (rétrocompatibilité avec les anciennes versions)
  const images: Array<HTMLImageElement> = Array.from(template?.querySelectorAll(selector) || []);
  images.forEach((image: HTMLImageElement) => {
    const style = image.getAttribute('style');
    if (style) {
      // Supprimer les propriétés max-width et width du style inline
      const newStyle = style.replace(/max-width\s*:\s*[^;]*;?\s*/g, '').replace(/width\s*:\s*[^;]*;?\s*/g, '').trim();
      if (newStyle) {
        image.setAttribute('style', newStyle);
      } else {
        image.removeAttribute('style');
      }
    }
  });

  // Ajouter max-width à la feuille de style dynamique
  addOrUpdateDynamicStyle(selector, { 'max-width': `${property.value}px` });
  // Ajouter max-width à la feuille de style dynamique
  addOrUpdateDynamicStyle(selector, { 'width': `${property.value}px` });

  // Note : l'attribut width est géré par le setter setAttribute dans la config
};

export const setSocialNetworkIconStyle = (selector: string, property: Property) => {
  const pattern = /^(https:\/\/media.shopimind.(?:com|io)\/img\/icons\/social\/)([0-9]{1,2})(\/[0-9]{1,2}\/.+)$/i;
  const template = getTemplateIframeDocument();
  const images: Array<HTMLImageElement> = Array.from(template?.querySelectorAll(selector));
  images.forEach((image: HTMLImageElement) => {
    image.setAttribute('src', image.getAttribute('src')?.replace(pattern, `$1${property.value}$3`) ?? '');
  });
};

// V4: utilise socialv2 sans dossier de taille
export const setSocialNetworkIconStyleV4 = (selector: string, property: Property, iconColor?: string) => {
  // Pattern pour socialv2 (nouveau chemin)
  const patternV2 = /^https:\/\/media\.shopimind\.(?:com|io)\/img\/icons\/socialv2\/[0-9]{1,2}\/(.+\.png)$/i;
  // Pattern pour social (ancien chemin) - pour migration
  const patternV1 = /^https:\/\/media\.shopimind\.(?:com|io)\/img\/icons\/social\/[0-9]{1,2}\/[0-9]{1,2}\/(.+)$/i;
  // Pattern pour URLs colorisées
  const patternColorized = /^https:\/\/media\.shopimind\.(?:com|io)\/clri\/i-[0-9]{1,2}\/[a-fA-F0-9]{6}\/(.+\.png)$/i;

  const template = getTemplateIframeDocument();
  const images: Array<HTMLImageElement> = Array.from(template?.querySelectorAll(selector));

  images.forEach((image: HTMLImageElement) => {
    const src = image.getAttribute('src');
    if (!src) return;

    // Ne pas modifier les icônes personnalisées (clients/)
    if (src.includes('/clients/')) {
      return;
    }

    let filename: string | null = null;

    // Extraire le nom du fichier selon le format de l'URL
    if (src.includes('/clri/')) {
      const match = src.match(patternColorized);
      if (match) filename = match[1];
    } else if (src.includes('/socialv2/')) {
      const match = src.match(patternV2);
      if (match) filename = match[1];
    } else if (src.includes('/social/')) {
      const match = src.match(patternV1);
      if (match) filename = match[1];
    }

    if (!filename) {
      return;
    }

    // Construire la nouvelle URL (standard ou colorisée)
    let newSrc: string;
    // Vérifier que iconColor est une couleur hex valide (avec ou sans #)
    const isValidColor = iconColor && /^#?[a-fA-F0-9]{6}$/.test(iconColor);
    if (isValidColor && CUSTOMIZABLE_ICON_STYLES_V4.includes(property.value) && iconColor) {
      const colorHex = iconColor.replace('#', '');
      newSrc = `https://media.shopimind.io/clri/i-${property.value}/${colorHex}/${filename}`;
    } else {
      newSrc = `https://media.shopimind.io/img/icons/socialv2/${property.value}/${filename}`;
    }

    image.setAttribute('src', newSrc);
  });
};

// V4: Applique la colorisation aux icônes via le script de colorisation
export const setSocialNetworkIconColorV4 = (selector: string, iconColor: string, iconStyle?: string) => {
  if (!iconColor) return;

  const template = getTemplateIframeDocument();
  const images: Array<HTMLImageElement> = Array.from(template?.querySelectorAll(selector));
  const colorHex = iconColor.replace('#', '');

  // Pattern pour détecter les URLs socialv2 standard
  const patternV2 = /^https:\/\/media\.shopimind\.(?:com|io)\/img\/icons\/socialv2\/([0-9]{1,2})\/(.+\.png)$/i;
  // Pattern pour détecter les URLs déjà colorisées
  const patternColorized = /^https:\/\/media\.shopimind\.(?:com|io)\/clri\/i-([0-9]{1,2})\/[a-fA-F0-9]{6}\/(.+\.png)$/i;

  images.forEach((image: HTMLImageElement) => {
    const src = image.getAttribute('src');
    if (!src) return;

    // Ne pas modifier les icônes personnalisées (clients/)
    if (src.includes('/clients/')) return;

    let style: string;
    let filename: string;

    // Vérifier si c'est une URL déjà colorisée
    const matchColorized = src.match(patternColorized);
    if (matchColorized) {
      style = matchColorized[1];
      filename = matchColorized[2];
    } else {
      // URL standard socialv2
      const matchV2 = src.match(patternV2);
      if (matchV2) {
        style = matchV2[1];
        filename = matchV2[2];
      } else {
        return; // URL non reconnue
      }
    }

    // Utiliser le style passé en paramètre si disponible
    if (iconStyle) {
      style = iconStyle;
    }

    // Construire la nouvelle URL colorisée
    const newSrc = `https://media.shopimind.io/clri/i-${style}/${colorHex}/${filename}`;
    image.setAttribute('src', newSrc);
  });
};

function createVerticalIcon(network: string, icon: HTMLAnchorElement): HTMLTableRowElement {
  const row: HTMLTableRowElement = document.createElement('tr');
  row.setAttribute('class', `${network} spm_social_icon_container`);
  const cell: HTMLTableCellElement = document.createElement('td');
  cell.setAttribute('class', 'spm_social_icon');
  cell.appendChild(icon);
  row.appendChild(cell);
  return row;
}

function createHorizontalIcon(network: string, icon: HTMLAnchorElement): HTMLTableCellElement {
  const cell: HTMLTableCellElement = document.createElement('td');
  cell.setAttribute('class', `${network} spm_social_icon_container spm_social_icon`);
  cell.appendChild(icon);
  return cell;
}

function createDisplayIcon(network: string, icon: HTMLAnchorElement): HTMLSpanElement {
  const span: HTMLSpanElement = document.createElement('span');
  span.setAttribute('class', `${network} spm_social_icon_container spm_social_icon`);
  span.appendChild(icon);
  return span;
}

function createTableContent(orientation: string, networks: Array<Network>): HTMLTableSectionElement {
  const tbody: HTMLTableSectionElement = document.createElement('tbody');
  if (orientation === 'vertical') {
    networks.forEach((network: Network) => {
      tbody.appendChild(createVerticalIcon(network.name, network.icon));
    });
  } else {
    const row: HTMLTableRowElement = document.createElement('tr');
    networks.forEach((network: Network) => {
      row.appendChild(createHorizontalIcon(network.name, network.icon));
    });
    tbody.appendChild(row);
  }
  return tbody;
}

interface NetworkOrSeparator {
  type: 'network' | 'separator';
  name: string;
  icon?: HTMLAnchorElement;
  padding?: string;
}

function extractPaddingValue(element: HTMLElement, orientation: 'horizontal' | 'vertical'): string {
  // Pour horizontal: le séparateur est un td avec padding-left/right
  // Pour vertical: le séparateur est un tr avec un td enfant qui a padding-top/bottom
  const style = element.getAttribute('style') || '';
  const innerTd = element.querySelector('td');
  const innerStyle = innerTd?.getAttribute('style') || '';

  // Chercher dans les deux sources possibles
  const allStyles = `${style} ${innerStyle}`;

  // Chercher padding-left, padding-right, padding-top, padding-bottom
  const paddingLeftMatch = allStyles.match(/padding-left:\s*([^;]+)/);
  const paddingTopMatch = allStyles.match(/padding-top:\s*([^;]+)/);

  if (orientation === 'horizontal' && paddingLeftMatch) {
    return paddingLeftMatch[1].trim();
  }
  if (orientation === 'vertical' && paddingTopMatch) {
    return paddingTopMatch[1].trim();
  }

  // Fallback: chercher n'importe quel padding trouvé
  if (paddingLeftMatch) return paddingLeftMatch[1].trim();
  if (paddingTopMatch) return paddingTopMatch[1].trim();

  return '5px';
}

function createTableContentWithSeparators(
  orientation: string,
  items: Array<NetworkOrSeparator>,
): HTMLTableSectionElement {
  const tbody: HTMLTableSectionElement = document.createElement('tbody');

  if (orientation === 'vertical') {
    items.forEach((item) => {
      if (item.type === 'separator') {
        // Créer séparateur vertical (padding top/bottom)
        const row = document.createElement('tr');
        row.setAttribute('class', 'spm_social_separator spm_social_icon_container');
        const cell = document.createElement('td');
        cell.setAttribute('style', `padding-top: ${item.padding}; padding-bottom: ${item.padding};`);
        row.appendChild(cell);
        tbody.appendChild(row);
      } else if (item.icon) {
        tbody.appendChild(createVerticalIcon(item.name, item.icon));
      }
    });
  } else {
    const row: HTMLTableRowElement = document.createElement('tr');
    items.forEach((item) => {
      if (item.type === 'separator') {
        // Créer séparateur horizontal (padding left/right)
        const cell = document.createElement('td');
        cell.setAttribute('class', 'spm_social_separator spm_social_icon_container');
        cell.setAttribute('style', `padding-left: ${item.padding}; padding-right: ${item.padding};`);
        row.appendChild(cell);
      } else if (item.icon) {
        row.appendChild(createHorizontalIcon(item.name, item.icon));
      }
    });
    tbody.appendChild(row);
  }
  return tbody;
}

export const setSocialNetworkOrientation = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const table = template.querySelector(selector);
  if (table) {
    const containers: HTMLElement[] = Array.from(table.querySelectorAll('.spm_social_icon_container'));

    // Déterminer l'orientation actuelle pour extraire le bon padding
    const currentOrientation = getClassByName(selector, 'spm_social_icons_') || 'horizontal';

    const items: NetworkOrSeparator[] = containers.map((container: HTMLElement) => {
      const classNames = container.getAttribute('class') || '';
      const isSeparator = classNames.includes('spm_social_separator');

      if (isSeparator) {
        return {
          type: 'separator' as const,
          name: 'spm_social_separator',
          padding: extractPaddingValue(container, currentOrientation as 'horizontal' | 'vertical'),
        };
      }

      return {
        type: 'network' as const,
        name: classNames.replace('spm_social_icon_container', '').replace('spm_social_icon', '').trim(),
        icon: container.querySelector('a') ?? document.createElement('a'),
      };
    });

    const tbody: HTMLElement | null = template?.querySelector(`${selector} > tbody`);
    if (tbody && property.value) {
      tbody.replaceWith(createTableContentWithSeparators(property.value, items));
    }
  }
};

const setActiveSocialNetworks = (selector: string, networks: Network[]) => {
  const template = getTemplateIframeDocument();
  const orientation = getClassByName(selector, 'spm_social_icons_');
  const tbody: HTMLElement = template?.querySelector(`${selector} > tbody`) as HTMLElement;
  if (tbody && orientation) {
    tbody.replaceWith(createTableContent(orientation, networks));
  }
};

/** SHARING SOCIAL NETWORK SETTERS */
export const getSocialNetworksLinks = (url: string, description = ''): StringMap => ({
  facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
  twitter: `https://twitter.com/intent/tweet?text=${description}&url=${url}`,
  email: `mailto:?subject=${description}&amp;body=${url}`,
  pinterest: `https://pinterest.com/pin/create/button/?url=${url}&description=${description}`,
  tumblr: `https://www.tumblr.com/share/link?url=${url}&amp;name=&amp;description=${description}`,
  // instagram: '',
  // youtube: '',
  // whatsapp: '',
  // snapchat: '',
});

function createSharingIcon(network: string, width: string, style: string, url: string, description: string): HTMLAnchorElement {
  const anchor = document.createElement('a');
  anchor.setAttribute('href', getSocialNetworksLinks(url, description)[network.replace('spm_', '')]);
  const image = document.createElement('img');
  image.setAttribute('src', `https://media.shopimind.io/img/icons/social/${style}/${width}/${network.replace('spm_', '')}.png`);
  image.setAttribute('width', width);
  anchor.appendChild(image);

  // TODO Create translation for url
  return anchor;
}

export const setActiveSharingSocialNetworks = (selector: string, property: Property) => {
  if (property.value) {
    const width = getAttributeByName(`${selector} img`, 'width') ?? DEFAULT_WIDTH;
    let style = getSocialNetworkIconStyle(`${selector} img`);
    if (!style) {
      style = '1';
    }
    const customUrl = getCustomUrl(selector);
    const customDescription = getCustomDescription(selector);
    const activeSocialNetworks: Array<string> = property.value;
    const networks: Network[] = activeSocialNetworks.map((network: string) => ({
      name: network,
      icon: createSharingIcon(network, width, style, customUrl, customDescription),
    }));
    setActiveSocialNetworks(selector, networks);
  }
};

export const setCustomUrl = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  // change twitter link if exists
  const twitterLink: HTMLElement|null = template?.querySelector(`${selector} .spm_twitter a`);
  if (twitterLink) {
    twitterLink.setAttribute('href', (twitterLink.getAttribute('href') ?? '').replace(twitterPattern, `$1$2$3${property.value}`));
  }
  // change email link if exists
  const emailLink: HTMLElement|null = template?.querySelector(`${selector} .spm_email a`);
  if (emailLink) {
    emailLink.setAttribute('href', (emailLink.getAttribute('href') ?? '').replace(emailPattern, `$1$2$3${property.value}`));
  }
  // change facebook link if exists
  const facebookLink: HTMLElement|null = template?.querySelector(`${selector} .spm_facebook a`);
  if (facebookLink) {
    facebookLink.setAttribute('href', (facebookLink.getAttribute('href') ?? '').replace(facebookPattern, `$1${property.value}`));
  }
  // change google link if exists
  const googleLink: HTMLElement|null = template?.querySelector(`${selector} .spm_googleplus a`);
  if (googleLink) {
    googleLink.setAttribute('href', (googleLink.getAttribute('href') ?? '').replace(googlePattern, `$1${property.value}`));
  }
};

export const setCustomDescription = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  // change twitter link if exists
  const twitterLink: HTMLElement|null = template?.querySelector(`${selector} .spm_twitter a`);
  if (twitterLink) {
    twitterLink.setAttribute('href', (twitterLink.getAttribute('href') ?? '').replace(twitterPattern, `$1${property.value}$3$4`));
  }
  // change email link if exists
  const emailLink: HTMLElement|null = template?.querySelector(`${selector} .spm_email a`);
  if (emailLink) {
    emailLink.setAttribute('href', (emailLink.getAttribute('href') ?? '').replace(emailPattern, `$1${property.value}$3$4`));
  }
};

/** FOLLOW SOCIAL NETWORK SETTERS */
function createFollowIcon(selector: string, network: string, width: string, style: string, link: string): HTMLAnchorElement {
  let translation: Record<string, any> | string = {};
  const oldElement = getTemplateIframeDocument().querySelector(selector);

  const anchor = document.createElement('a');
  anchor.setAttribute('href', link);
  const image = document.createElement('img');
  image.setAttribute('src', `https://media.shopimind.io/img/icons/social/${style}/${width}/${network.replace('spm_', '')}.png`);
  image.setAttribute('width', width);

  // Check if translation already exists
  const oldLink = oldElement?.querySelector(`.${network} a`);
  if (oldLink && oldLink.hasAttribute(`${TRANSLATION_ATTRIBUTE}-href`)) {
    translation = {
      attribute: `${TRANSLATION_ATTRIBUTE}-href`,
      value: oldLink.getAttribute(`${TRANSLATION_ATTRIBUTE}-href`),
    };
  } else {
    // Create translation for link
    const translationVariable = network.replace('spm_', '') === 'email' ? 'contact' : network.replace('spm_', '');
    const { newHtml } = generateTranslations(`<a href="{var=social.${translationVariable}_url}"></a>`, { newElement: true });
    const $ = cheerio.load(newHtml);
    translation = {
      attribute: `${TRANSLATION_ATTRIBUTE}-href`,
      value: $('a').attr(`${TRANSLATION_ATTRIBUTE}-href`),
    };
  }

  anchor.appendChild(image);

  // Add translation property on new element
  if (typeof translation === 'object' && 'attribute' in translation && translation.attribute) {
    anchor.setAttribute(translation.attribute, translation.value);
  }

  return anchor;
}

export const setActiveFollowSocialNetworks = (selector: string, property: Property) => {
  if (property.value) {
    const width = getAttributeByName(`${selector} img`, 'width') ?? DEFAULT_WIDTH;
    let style = getSocialNetworkIconStyle(`${selector} img`);
    if (!style) {
      style = '1';
    }
    const activeSocialNetworks: Array<string> = property.value;
    const networks: Network[] = activeSocialNetworks.map((network: string) => ({
      name: network,
      icon: createFollowIcon(selector, network, width, style, getLinkByNetwork(selector, network)),
    }));
    setActiveSocialNetworks(selector, networks);
  }
};

export const setActiveFollowSocialNetworksForDisplay = (selector: string, property: Property) => {
  if (property.value) {
    const width = getAttributeByName(`${selector} img`, 'width') ?? DEFAULT_WIDTH;
    let style = getSocialNetworkIconStyle(`${selector} img`);
    if (!style) {
      style = '1';
    }
    const activeSocialNetworks: Array<string> = property.value;
    const template = getTemplateIframeDocument();
    const container = template.querySelector(selector);
    if (container) {
      const newContainer = document.createElement('div');
      newContainer.setAttribute('class', container.getAttribute('class') ?? '');
      activeSocialNetworks.forEach((network: string) => {
        const icon = createFollowIcon(selector, network, width, style, getLinkByNetwork(selector, network));
        newContainer.appendChild(createDisplayIcon(network, icon));
      });
      container.replaceWith(newContainer);
    }
  }
};

export const setSocialNetworkLinks = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const networks: HTMLElement|null = template?.querySelector(selector);
  property.value.forEach((network: InputGroupField) => {
    const link: HTMLAnchorElement = networks?.querySelector(`.${network.key} a`) as HTMLAnchorElement;
    if (link) {
      link.setAttribute('href', network.value);
    }
  });
};

export const sortSocialNetworks = (selector: string, property: Property) => {
  const template = getTemplateIframeDocument();
  const parent: HTMLElement = template?.querySelector(selector) as HTMLElement;
  const networks: HTMLElement[] = Array.from(parent?.querySelectorAll('.spm_social_icon_container'));
  const sortedIds = property.value.map((element: InputGroupField) => element.key);

  networks.sort((a: Element, b: Element) => { // Sort fields in updated order
    const firstIndex = a.getAttribute('class')?.replace('spm_social_icon_container', '').replace('spm_social_icon', '').replace(new RegExp('spm_hide', 'g'), '');
    const secondIndex = b.getAttribute('class')?.replace('spm_social_icon_container', '').replace('spm_social_icon', '').replace(new RegExp('spm_hide', 'g'), '');
    if (firstIndex && secondIndex) {
      if (sortedIds.indexOf(firstIndex) === -1 || sortedIds.indexOf(secondIndex) === -1) return 1;
      return sortedIds.indexOf(firstIndex) - sortedIds.indexOf(secondIndex);
    }
    return 1;
  })
    .forEach((field: Element) => {
      if (parent) parent.append(field);
    });
};

/** V4 SOCIAL NETWORKS SETTERS */
interface SocialNetworkItemV4 {
  key: string;
  network: string;
  url: string;
  customIcon?: string;
  label?: string;
}

function getColorFromExistingIcons(selector: string): string | null {
  const template = getTemplateIframeDocument();
  const existingImg = template?.querySelector(`${selector} img`) as HTMLImageElement;
  if (existingImg) {
    const src = existingImg.getAttribute('src');
    if (src) {
      // Pattern pour détecter les URLs colorisées: /clri/i-{style}/{color}/{filename}
      const match = src.match(/\/clri\/i-[0-9]{1,2}\/([a-fA-F0-9]{6})\//);
      if (match) {
        return match[1]; // Retourne le code couleur hex
      }
    }
  }
  return null;
}

function createFollowIconV4(
  selector: string,
  network: string,
  width: string,
  style: string,
  link: string,
  customIcon?: string,
  existingLink?: Element | null,
): HTMLAnchorElement {
  let hrefTranslation: Record<string, any> | string = {};
  const existingImg = existingLink?.querySelector('img');

  const anchor = document.createElement('a');
  anchor.setAttribute('href', link);

  // Si c'est une icône custom sans URL, ne pas créer d'image
  const isCustomWithoutIcon = network === 'custom' && !customIcon;

  if (!isCustomWithoutIcon) {
    const image = document.createElement('img');

    if (customIcon) {
      image.setAttribute('src', customIcon);
    } else {
      // Vérifier si une couleur est appliquée aux icônes existantes
      const existingColor = getColorFromExistingIcons(selector);
      const networkName = network.replace('spm_', '');

      if (existingColor && CUSTOMIZABLE_ICON_STYLES_V4.includes(style)) {
        // Utiliser l'URL colorisée
        image.setAttribute('src', `https://media.shopimind.io/clri/i-${style}/${existingColor}/${networkName}.png`);
      } else {
        // V4 utilise socialv2 sans dossier de taille (le CSS gère la taille)
        image.setAttribute('src', `https://media.shopimind.io/img/icons/socialv2/${style}/${networkName}.png`);
      }
    }
    image.setAttribute('width', width);
    image.setAttribute('style', `max-width: ${width}px`);

    // Préserver la traduction src de l'image existante
    if (existingImg && existingImg.hasAttribute(`${TRANSLATION_ATTRIBUTE}-src`)) {
      image.setAttribute(`${TRANSLATION_ATTRIBUTE}-src`, existingImg.getAttribute(`${TRANSLATION_ATTRIBUTE}-src`) || '');
    }

    anchor.appendChild(image);
  }

  // Utiliser le lien existant passé en paramètre (basé sur l'index) pour récupérer la traduction href
  if (existingLink && existingLink.hasAttribute(`${TRANSLATION_ATTRIBUTE}-href`)) {
    hrefTranslation = {
      attribute: `${TRANSLATION_ATTRIBUTE}-href`,
      value: existingLink.getAttribute(`${TRANSLATION_ATTRIBUTE}-href`),
    };
  } else if (!customIcon) {
    // Nouvel élément : générer une nouvelle traduction
    const translationVariable = network === 'email' ? 'contact' : network;
    const { newHtml } = generateTranslations(`<a href="{var=social.${translationVariable}_url}"></a>`, { newElement: true });
    const $ = cheerio.load(newHtml);
    hrefTranslation = {
      attribute: `${TRANSLATION_ATTRIBUTE}-href`,
      value: $('a').attr(`${TRANSLATION_ATTRIBUTE}-href`),
    };
  }

  if (typeof hrefTranslation === 'object' && 'attribute' in hrefTranslation && hrefTranslation.attribute) {
    anchor.setAttribute(hrefTranslation.attribute, hrefTranslation.value);
  }

  return anchor;
}

function createHorizontalIconV4(key: string, network: string, icon: HTMLAnchorElement, isCustom: boolean): HTMLTableCellElement {
  const cell: HTMLTableCellElement = document.createElement('td');
  const classNames = isCustom ? `spm_${key} spm_social_icon_container spm_social_icon` : `spm_${network} spm_social_icon_container spm_social_icon`;
  cell.setAttribute('class', classNames);
  if (isCustom) {
    cell.setAttribute('data-custom-icon', 'true');
  }
  cell.appendChild(icon);
  return cell;
}

interface SeparatorConfig {
  paddingHorizontal: string;
  paddingVertical: string;
}

function createHorizontalSeparatorV4(key: string, config: SeparatorConfig): HTMLTableCellElement {
  const cell: HTMLTableCellElement = document.createElement('td');
  cell.setAttribute('class', 'spm_social_separator spm_social_icon_container');
  cell.setAttribute('style', `padding-left: ${config.paddingHorizontal}; padding-right: ${config.paddingHorizontal};`);
  return cell;
}

function createVerticalSeparatorV4(key: string, config: SeparatorConfig): HTMLTableRowElement {
  const row: HTMLTableRowElement = document.createElement('tr');
  row.setAttribute('class', 'spm_social_separator spm_social_icon_container');

  const cell: HTMLTableCellElement = document.createElement('td');
  cell.setAttribute('style', `padding-top: ${config.paddingVertical}; padding-bottom: ${config.paddingVertical};`);

  row.appendChild(cell);
  return row;
}

function createVerticalIconV4(key: string, network: string, icon: HTMLAnchorElement, isCustom: boolean): HTMLTableRowElement {
  const row: HTMLTableRowElement = document.createElement('tr');
  const classNames = isCustom ? `spm_${key} spm_social_icon_container` : `spm_${network} spm_social_icon_container`;
  row.setAttribute('class', classNames);
  if (isCustom) {
    row.setAttribute('data-custom-icon', 'true');
  }
  const cell: HTMLTableCellElement = document.createElement('td');
  cell.setAttribute('class', 'spm_social_icon');
  cell.appendChild(icon);
  row.appendChild(cell);
  return row;
}

interface SocialNetworkItemForRender {
  key: string;
  network: string;
  icon?: HTMLAnchorElement;
  isCustom: boolean;
  isSeparator: boolean;
  separatorConfig?: SeparatorConfig;
}

function createTableContentV4(orientation: string, items: Array<SocialNetworkItemForRender>): HTMLTableSectionElement {
  const tbody: HTMLTableSectionElement = document.createElement('tbody');
  if (orientation === 'vertical') {
    items.forEach((item) => {
      if (item.isSeparator && item.separatorConfig) {
        tbody.appendChild(createVerticalSeparatorV4(item.key, item.separatorConfig));
      } else if (item.icon) {
        tbody.appendChild(createVerticalIconV4(item.key, item.network, item.icon, item.isCustom));
      }
    });
  } else {
    const row: HTMLTableRowElement = document.createElement('tr');
    items.forEach((item) => {
      if (item.isSeparator && item.separatorConfig) {
        row.appendChild(createHorizontalSeparatorV4(item.key, item.separatorConfig));
      } else if (item.icon) {
        row.appendChild(createHorizontalIconV4(item.key, item.network, item.icon, item.isCustom));
      }
    });
    tbody.appendChild(row);
  }
  return tbody;
}

export const setSocialNetworkItemsV4 = (selector: string, property: Property) => {
  if (!property.value || !Array.isArray(property.value)) return;

  const template = getTemplateIframeDocument();
  const orientation = getClassByName(selector, 'spm_social_icons_') || 'horizontal';
  const width = getAttributeByName(`${selector} img`, 'width') ?? DEFAULT_WIDTH;
  // Utiliser le getter v4 pour récupérer le style actuel
  let style = getSocialNetworkIconStyleV4(`${selector} img`);
  if (!style) {
    style = '1';
  }

  // Récupérer tous les liens existants dans l'ordre pour préserver les traductions par index
  const existingLinks = Array.from(template?.querySelectorAll(`${selector} .spm_social_icon_container a`) || []);
  let linkIndex = 0;

  const items: Array<SocialNetworkItemForRender> = property.value.map((item: any) => {
    const isSeparator = item.network === 'separator';

    if (isSeparator) {
      // Créer un séparateur (pas de traduction, pas de lien)
      return {
        key: item.key,
        network: 'separator',
        isCustom: false,
        isSeparator: true,
        separatorConfig: {
          paddingHorizontal: item.separatorPadding || '5px',
          paddingVertical: item.separatorPadding || '5px',
        },
      };
    }

    const isCustom = item.network === 'custom';
    const url = item.url || item.value || '';
    const icon = createFollowIconV4(
      selector,
      item.network,
      width,
      style,
      url,
      isCustom ? item.customIcon : undefined,
      existingLinks[linkIndex] || null,
    );
    linkIndex += 1;

    return {
      key: item.key,
      network: item.network,
      icon,
      isCustom,
      isSeparator: false,
    };
  });

  const tbody: HTMLElement = template?.querySelector(`${selector} > tbody`) as HTMLElement;
  if (tbody && items.length > 0) {
    tbody.replaceWith(createTableContentV4(orientation, items));

    // Mettre à jour le state et supprimer les traductions orphelines
    updateSectionsInState(false);
    removeUnusedTranslations();
  }
};

/** V4 DISPLAY (EMBED/POPUP) SOCIAL NETWORKS SETTER */
function createDisplayIconV4(key: string, network: string, icon: HTMLAnchorElement, isCustom: boolean): HTMLSpanElement {
  const span: HTMLSpanElement = document.createElement('span');
  const classNames = isCustom
    ? `spm_${key} spm_social_icon_container spm_social_icon`
    : `spm_${network} spm_social_icon_container spm_social_icon`;
  span.setAttribute('class', classNames);
  if (isCustom) {
    span.setAttribute('data-custom-icon', 'true');
  }
  span.appendChild(icon);
  return span;
}

function createDisplaySeparatorV4(padding: string): HTMLSpanElement {
  const span: HTMLSpanElement = document.createElement('span');
  span.setAttribute('class', 'spm_social_separator spm_social_icon_container');
  span.setAttribute('style', `padding-left: ${padding}; padding-right: ${padding};`);
  return span;
}

export const setSocialNetworkItemsV4ForDisplay = (selector: string, property: Property) => {
  if (!property.value || !Array.isArray(property.value)) return;

  const template = getTemplateIframeDocument();
  const width = getAttributeByName(`${selector} img`, 'width') ?? DEFAULT_WIDTH;
  let style = getSocialNetworkIconStyleV4(`${selector} img`);
  if (!style) {
    style = '1';
  }

  const existingLinks = Array.from(template?.querySelectorAll(`${selector} .spm_social_icon_container a`) || []);
  let linkIndex = 0;

  const container = template.querySelector(selector);
  if (!container) return;

  const newContainer = document.createElement('div');
  newContainer.setAttribute('class', container.getAttribute('class') ?? '');

  property.value.forEach((item: any) => {
    if (item.network === 'separator') {
      newContainer.appendChild(createDisplaySeparatorV4(item.separatorPadding || '5px'));
      return;
    }

    const isCustom = item.network === 'custom';
    const url = item.url || item.value || '';
    const icon = createFollowIconV4(
      selector,
      item.network,
      width,
      style,
      url,
      isCustom ? item.customIcon : undefined,
      existingLinks[linkIndex] || null,
    );
    linkIndex += 1;

    newContainer.appendChild(createDisplayIconV4(item.key, item.network, icon, isCustom));
  });

  container.replaceWith(newContainer);

  updateSectionsInState(false);
  removeUnusedTranslations();
};
